<script setup lang="ts">
    import { computed, ref, onMounted, watch } from 'vue'
    import { onBeforeRouteLeave, useRoute } from 'vue-router'
    import { SavingState } from '@common/Common'
    import EditableValueSeriesSheet from '@common/Components/EditableValueSeriesSheet.vue'
    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import Navigation from '@common/Navigation.vue'
    import SaveButton from '@common/Components/SaveButton.vue'
    import ValueSeriesTable from '@Quest/_Common/Components/ValueSeriesTable.vue'
    import ProjectNavigation from '@Quest/ProjectPlans/Components/ProjectNavigation.vue'
    import ProjectContextHeader from '@Quest/ProjectPlans/Components/ProjectContextHeader.vue'
    import { useProjectResourceUnitCostsQueryV1 } from '@Quest/ProjectPlans/queries-vue-composables'
    import { useUpdateProjectResourceUnitCostsCommandV1 } from '@Quest/ProjectPlans/commands-vue-composables'
    import type { ResourceUnitCostsPageReadModel } from '@Quest/ProjectPlans/readmodels'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const projectId = computed(() => String(route.params.projectId ?? ''))

    const rm = ref<ResourceUnitCostsPageReadModel | null>(null)
    const projectPlanId = computed<string | null>(() => rm.value?.ProjectPlanId ?? null)
    const fromYear = computed<number | null>(() => rm.value?.FromYear ?? null)

    const projectName = computed(() => rm.value?.ProjectName ?? '')
    const projectPlanName = computed<string>(() => rm.value?.ProjectPlanName ?? '')
    const activePlanId = computed<string | null>(() => rm.value?.ActiveProjectPlanId ?? null)

    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const userId = computed(() => rm.value?.UserId ?? 0)
    const isWorkspaceMember = computed(() => rm.value?.IsWorkspaceMember ?? false)
    const isProjectOwner = computed(() => rm.value?.IsProjectOwner ?? false)

    const isEditing = ref(false)
    const savingState = ref<SavingState>(SavingState.Saved)
    const editingComment = ref<string | null>(null)
    const sheet = ref<any>(null)

    const title = computed(() =>`${rm.value?.Title} (${rm.value?.CurrencyLabel})`)
    const stopEditingLabel = computed(() => savingState.value === SavingState.Pending ? 'Cancel editing' : 'Back')
    const inputSeries = computed(() => rm.value?.Series.filter(a => !a.IsTotal) ?? [])
    const isSubmitted = computed(() => rm.value?.IsSubmitted ?? false)
    const comment = computed(() => rm.value?.Comment ?? null)

    const {
        runProjectResourceUnitCostsQueryV1,
        modelProjectResourceUnitCostsQueryV1
    } = useProjectResourceUnitCostsQueryV1(workspaceId, tchProjectId, projectId, projectPlanId)

    const {
        invokeUpdateProjectResourceUnitCostsCommandV1,
        onSucceedUpdateProjectResourceUnitCostsCommandV1
    } = useUpdateProjectResourceUnitCostsCommandV1(userId, workspaceId, portfolioId, projectId, projectPlanId, fromYear)

    function startEditing()
    {
        if (!rm.value) return
        editingComment.value = rm.value.Comment
        isEditing.value = true
    }

    function cancelEditing()
    {
        if (savingState.value === SavingState.Saved)
        {
            isEditing.value = false
        }
        else if (
            savingState.value === SavingState.Pending &&
            confirm('You have unsaved changes. Are you sure you want to cancel editing?')
        )
        {
            savingState.value = SavingState.Saved
            isEditing.value = false
        }
    }

    function onDataChange()
    {
        savingState.value = SavingState.Pending
    }

    function handleSaveCommand()
    {
        if (!rm.value) return

        const entries = sheet.value?.getEntriesForSave()

        invokeUpdateProjectResourceUnitCostsCommandV1({
            entries,
            comment: editingComment.value
        })

        savingState.value = SavingState.InProgress
    }




    onMounted(() =>
    {
        document.title = 'Resource costs | TransparentChoice'
        
        runProjectResourceUnitCostsQueryV1({ 
            ProjectPlanId: route.params.planId ? String(route.params.planId) : null 
        })
    })

    watch(() => route.params.planId, () => 
    {
        runProjectResourceUnitCostsQueryV1({
            ProjectPlanId: route.params.planId as string | null
        })
    })

    watch(modelProjectResourceUnitCostsQueryV1, (current) =>
    {
        if (current?.RedirectUrl)
        {
            window.location.replace(current.RedirectUrl)
        }

        if (current)
        {
            rm.value = current
        }
    })


    onSucceedUpdateProjectResourceUnitCostsCommandV1(() =>
    {
        runProjectResourceUnitCostsQueryV1()

        if (savingState.value === SavingState.InProgress)
        {
            savingState.value = SavingState.Saved
        }
    })

    onBeforeRouteLeave((_to, _from, next) =>
    {
        if (savingState.value === SavingState.Pending)
        {
            if (confirm('You have unsaved changes. Do you want to save them before leaving?'))
            {
                handleSaveCommand()
                next()
            }
            else
            {
                next()
            }
        }
        else
        {
            next()
        }
    })
</script>

<template>
    <div>
        <PortfolioNavigation v-if="isWorkspaceMember" :workspaceId="workspaceId" :projectId="tchProjectId" />
        <Navigation v-else-if="isProjectOwner" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <ProjectContextHeader :projectName="projectName"
                                          :planId="projectPlanId"
                                          :planName="projectPlanName"
                                          :activePlanId="activePlanId" />
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <SaveButton v-if="isEditing" class="mr-1" :savingState="savingState" @saveChanges="handleSaveCommand()"></SaveButton>
                    <button v-if="isEditing" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="cancelEditing">{{stopEditingLabel}}</button>

                    <!-- When NOT submitted -->
                    <button class="btn btn-success"
                            style="padding: 0.7em 2em;"
                            v-if="!isEditing && !isSubmitted"
                            @click="startEditing">
                        Edit unit costs
                    </button>

                    <!-- When submitted -->
                    <button class="btn btn-outline-secondary"
                            style="padding: 0.7em 2em; cursor: default;"
                            v-if="!isEditing && isSubmitted"
                            disabled
                            title="You cannot edit a submitted plan">
                        <i class="fas fa-lock mr-2"></i> Submitted
                    </button>
                </div>
            </div>
            <hr />

            <ProjectNavigation ActivePage="resources" class="mt-4"
                               :workspaceId="workspaceId"
                               :tchProjectId="tchProjectId"
                               :projectId="projectId"
                               :planId="projectPlanId" />

            <div v-if="isEditing">
                <div class="form-group mb-4">
                    <label for="comment" class="mb-1 text-muted" style="font-weight: 400; font-size: 0.97em;">
                        Assumptions or notes (optional)
                    </label>
                    <textarea id="comment"
                              v-model.trim="editingComment"
                              @input="onDataChange"
                              rows="3"
                              class="form-control">
                    </textarea>
                </div>

                <div class="alert alert-warning mb-1">
                    <strong>Note:</strong> Formula entered below will save as values.
                </div>

                <EditableValueSeriesSheet ref="sheet" :propTitle="title" :propSeries="inputSeries" :propColumns="rm.Columns" @dataChange="onDataChange" />
            </div>
            <div v-else>
                <div class="toolbar">
                    <div>
                        <div class="btn-group" role="group">
                            <router-link :to="{name: 'q-project-resource-allocations-path', params: { workspaceId, tchProjectId, projectId, planId: projectPlanId }, query: route.query }"
                                         class="btn btn-outline-secondary">
                                Allocations
                            </router-link>
                            <router-link :to="{name: 'q-project-resource-savings-path', params: { workspaceId, tchProjectId, projectId, planId: projectPlanId }, query: route.query }"
                                         class="btn btn-outline-secondary">
                                Savings
                            </router-link>
                            <span class="btn btn-secondary disabled">
                                Unit costs
                            </span>
                        </div>
                    </div>
                    <div></div>
                </div>

                <div v-if="comment" class="alert alert-info mt-3 mb-3" style="white-space: pre-line;">
                    <strong>Assumptions / Notes:</strong>
                    {{ comment }}
                </div>

                <ValueSeriesTable :propTitle="title" :propSeries="rm.Series" :propColumns="rm.Columns" />
            </div>
        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>
